import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { getLanguageName } from "../../types";
import * as Popover from "@radix-ui/react-popover";
import Button, { Color, Size } from "./Button";
import { IoChevronDown, IoChevronUp } from "react-icons/io5";
import { Languages } from "./LanguageMultiSelect";
import classNames from "./LanguageSelect.module.css";
import { useIsMobile } from "../../hooks/layout";

function LanguageSelect(props: {
  selectedLanguage: string;
  onChange: (value: string) => void;
  languages: string[];
  trigger?: ReactNode | ReactNode[];
  size?: Size;
  color?: Color;
  backgroundColor?: Color;
  borderColor?: Color;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const changeLanguage = useCallback(
    (language: string) => {
      props.onChange(language);
      setIsOpen(false);
    },
    [props, setIsOpen]
  );

  const triggerRef = useRef<HTMLButtonElement>(null);
  const [triggerWidth, setTriggerWidth] = useState<number | undefined>();

  useEffect(() => {
    if (triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, []);

  const isMobile = useIsMobile();

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild ref={triggerRef} className={classNames.Trigger}>
        {props.trigger ?? (
          <Button
            size={props.size}
            color={props.color}
            borderColor={props.borderColor}
            backgroundColor={props.backgroundColor}
          >
            <span>{getLanguageName(props.selectedLanguage)}</span>
            {!isOpen ? <IoChevronDown /> : <IoChevronUp />}
          </Button>
        )}
      </Popover.Trigger>
      <Popover.Portal>
        <Languages
          type="single"
          style={{
            minWidth: isMobile ? triggerWidth : undefined,
          }}
          languages={props.languages}
          selectedLanguages={[props.selectedLanguage]}
          onSelectLanguage={(language) => {
            changeLanguage(language);
          }}
          onClose={() => setIsOpen(false)}
        />
      </Popover.Portal>
    </Popover.Root>
  );
}

export default LanguageSelect;
